/**
 * @file Inline SVG icons. Keeping them inline (not webfont/sprite) so they
 * inherit currentColor and CSP `script-src 'self'` doesn't need extra origins.
 */

import { el } from './utils.js';
import { svgFromMarkup } from './lib/svg.js';

function svg(viewBox, paths) {
  // Set intrinsic dimensions so the SVG doesn't inflate to fill its
  // container when no explicit width/height comes from CSS. 1em scales
  // with the parent's font-size, so an icon inside `<button>` (14-16 px)
  // is a sensible glyph and an icon inside larger contexts (empty state,
  // headers) can opt in with explicit CSS.
  return svgFromMarkup(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="1em" height="1em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="miter" aria-hidden="true">${paths}</svg>`);
}

export const Icons = {
  search:    () => svg('0 0 24 24', '<circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>'),
  bell:      () => svg('0 0 24 24', '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10 21a2 2 0 0 0 4 0"/>'),
  close:     () => svg('0 0 24 24', '<path d="M18 6 6 18M6 6l12 12"/>'),
  laptop:    () => svg('0 0 24 24', '<rect x="3" y="4" width="18" height="12"/><path d="M2 20h20"/>'),
  phone:     () => svg('0 0 24 24', '<rect x="7" y="2" width="10" height="20"/><path d="M11 18h2"/>'),
  keyboard:  () => svg('0 0 24 24', '<rect x="2" y="6" width="20" height="12"/><path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10"/>'),
  mouse:     () => svg('0 0 24 24', '<rect x="6" y="3" width="12" height="18"/><path d="M12 7v4"/>'),
  monitor:   () => svg('0 0 24 24', '<rect x="3" y="4" width="18" height="12"/><path d="M8 20h8M12 16v4"/>'),
  bolt:      () => svg('0 0 24 24', '<path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z"/>'),
  signal:    () => svg('0 0 24 24', '<path d="M2 20h2M7 20h2M12 4v16M17 9v11M22 14v6"/>'),
  card:      () => svg('0 0 24 24', '<rect x="2" y="5" width="20" height="14"/><path d="M2 10h20"/>'),
  ai:        () => svg('0 0 24 24', '<path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M16 16l3 3M5 19l3-3M16 8l3-3"/><circle cx="12" cy="12" r="4"/>'),
  multisport: () => svg('0 0 24 24', '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/>'),
  note:      () => svg('0 0 24 24', '<path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6M8 13h8M8 17h6"/>'),
  plus:      () => svg('0 0 24 24', '<path d="M12 5v14M5 12h14"/>'),
  arrow_left: () => svg('0 0 24 24', '<path d="M19 12H5M12 19l-7-7 7-7"/>'),
  chevron_left:  () => svg('0 0 24 24', '<path d="m15 6-6 6 6 6"/>'),
  chevron_right: () => svg('0 0 24 24', '<path d="m9 6 6 6-6 6"/>'),
  refresh:   () => svg('0 0 24 24', '<path d="M21 12a9 9 0 1 1-3-6.7M21 4v5h-5"/>'),
  download:  () => svg('0 0 24 24', '<path d="M12 3v12M7 10l5 5 5-5M5 21h14"/>'),
  settings:  () => svg('0 0 24 24', '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>'),
  user:      () => svg('0 0 24 24', '<circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/>'),
  inbox:     () => svg('0 0 24 24', '<path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.5 5h13l3.5 7v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-6Z"/>'),
  history:   () => svg('0 0 24 24', '<path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l3 2"/>'),
  clock:     () => svg('0 0 24 24', '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>'),
  more:      () => svg('0 0 24 24', '<path d="M5 12h.5M12 12h.5M19 12h.5"/>'),
};

/**
 * Look up the icon for a category. Falls back to a generic note icon.
 */
export function categoryIcon(category) {
  const map = {
    laptop: Icons.laptop,
    phone: Icons.phone,
    keyboard: Icons.keyboard,
    mouse: Icons.mouse,
    ext_screen: Icons.monitor,
    charger: Icons.bolt,
    phone_tariff: Icons.signal,
    access_card: Icons.card,
    ai_tool: Icons.ai,
    multisport: Icons.multisport,
    notes: Icons.note,
  };
  return (map[category] || Icons.note)();
}

/** Wrap an icon in a span so it can sit inline with text without alignment hacks. */
export function inlineIcon(iconFactory) {
  return el('span', { class: 'inline-icon', style: 'display:inline-flex;vertical-align:middle' }, iconFactory());
}
